import { EVENTS, events } from '../emitter'
import { createStore } from './create'
import { galleryApi } from './data/gallery'
import { toastStore } from './toasts'
import { GalleryImage } from '/common/types/aifans-schema'

type GalleryState = {
  images: GalleryImage[]
  loading: boolean
  partial: boolean
  showAllImages: boolean
  showFlaggedImages: boolean
  page: number
  hasMore: boolean
}

const initState: GalleryState = {
  images: [],
  loading: true,
  partial: false,
  showAllImages: false,
  showFlaggedImages: false,
  page: 1,
  hasMore: true,
}

export const galleryStore = createStore<GalleryState>(
  'gallery',
  initState
)((get, set) => {
  events.on(EVENTS.loggedOut, () => {
    galleryStore.setState(initState)
  })

  events.on(EVENTS.init, (init) => {
    galleryStore.setState({ images: init.images || [], loading: false })
  })

  return {
    async *getGallery({ loading, images: prev, partial, showAllImages, showFlaggedImages }) {
      if (loading) return

      yield { loading: true, images: partial ? [] : prev }
      const res = await galleryApi.getGallery(showAllImages, showFlaggedImages)
      if (res.result) {
        yield { loading: false, images: res.result.images || [] }
      }

      if (res.error) {
        yield { loading: false }
        toastStore.error(`Failed to retrieve gallery images: ${res.error}`)
      }
    },

    async *initPaged({ showAllImages, showFlaggedImages }) {
      const pageSize = 24
      yield { loading: true, page: 1 }
      const res = await galleryApi.getGallery(showAllImages, showFlaggedImages, 1, pageSize)
      if (res.result) {
        const imgs = res.result.images || []
        yield { loading: false, images: imgs, page: 1, hasMore: imgs.length === pageSize }
      }

      if (res.error) {
        yield { loading: false }
        toastStore.error(`Failed to retrieve gallery images: ${res.error}`)
      }
    },

    async *loadMore({ page, images, showAllImages, showFlaggedImages, hasMore, loading }) {
      if (loading || !hasMore) return
      const nextPage = page + 1
      const pageSize = 24
      yield { loading: true }
      const res = await galleryApi.getGallery(showAllImages, showFlaggedImages, nextPage, pageSize)
      if (res.result) {
        const nextImages = res.result.images || []
        yield {
          loading: false,
          images: [...images, ...nextImages],
          page: nextPage,
          hasMore: nextImages.length === pageSize,
        }
      }

      if (res.error) {
        yield { loading: false }
        toastStore.error(`Failed to retrieve more images: ${res.error}`)
      }
    },

    toggleShowAllImages({ showAllImages }) {
      return { showAllImages: !showAllImages, page: 1, hasMore: true }
    },

    setShowAllImages(_, showAllImages: boolean) {
      return { showAllImages }
    },

    toggleShowFlaggedImages({ showFlaggedImages }) {
      return { showFlaggedImages: !showFlaggedImages, page: 1, hasMore: true }
    },

    setShowFlaggedImages(_, showFlaggedImages: boolean) {
      return { showFlaggedImages }
    },

    async *flagImage({ loading, images }, imageId: string, flagReason?: string) {
      if (loading) return

      const res = await galleryApi.flagImage(imageId, flagReason)
      if (res.result) {
        toastStore.success('Image flagged successfully')
        // Update the image in the local state
        const updatedImages = images.map((img) =>
          img._id === imageId
            ? {
                ...img,
                flagged: true,
                flaggedBy: '',
                flaggedAt: new Date().toISOString(),
                flagReason: flagReason || 'Content violation',
              }
            : img
        )
        yield { images: updatedImages }
      }

      if (res.error) {
        toastStore.error(`Failed to flag image: ${res.error}`)
      }
    },

    async *unflagImage({ loading, images }, imageId: string) {
      if (loading) return

      const res = await galleryApi.unflagImage(imageId)
      if (res.result) {
        toastStore.success('Image unflagged successfully')
        // Update the image in the local state
        const updatedImages = images.map((img) => {
          if (img._id === imageId) {
            const { flagged, flaggedBy, flaggedAt, flagReason, ...rest } = img
            return rest as typeof img
          }
          return img
        })
        yield { images: updatedImages }
      }

      if (res.error) {
        toastStore.error(`Failed to unflag image: ${res.error}`)
      }
    },

    async *deleteImage({ loading, images }, imageId: string) {
      if (loading) return

      const res = await galleryApi.deleteImage(imageId)
      if (res.result) {
        toastStore.success('Image deleted successfully')
        // Remove the image from the local state
        const updatedImages = images.filter((img) => img._id !== imageId)
        yield { images: updatedImages }
      }

      if (res.error) {
        toastStore.error(`Failed to delete image: ${res.error}`)
      }
    },

    // async *create(
    //   { images: prev, loading },
    //   scenario: NewScenario,
    //   onSuccess?: (scenario: AppSchema.ScenarioBook) => void,
    //   onFailure?: (scenario: NewScenario) => void
    // ) {
    //   if (loading) return
    //   yield { loading: true }
    //   const res = await scenarioApi.createScenario(scenario)
    //   if (res.error) {
    //     toastStore.error(`Could not create scenario: ${res.error}`)
    //     yield { loading: false }
    //     onFailure?.(scenario)
    //     return
    //   }

    //   if (res.result) {
    //     yield { loading: false, images: [...prev, res.result] }
    //     toastStore.success('Created scenario')
    //     onSuccess?.(res.result)
    //     return
    //   }
    // },

    // async *remove({ loading, scenarios: prev }, scenarioId: string, onSuccess?: Function) {
    //   if (loading) {
    //     toastStore.error('Cannot remove scenario while loading')
    //     return
    //   }
    //   yield { loading: true }
    //   const res = await scenarioApi.removeScenario(scenarioId)

    //   if (res.error) {
    //     toastStore.error(`Failed to remove scenario: ${res.error}`)
    //     yield { loading: false }
    //   }

    //   if (res.result) {
    //     const next = prev.filter((scenario) => scenario._id !== scenarioId)
    //     yield { loading: false, scenarios: next }
    //     toastStore.success('Scenario deleted')
    //     onSuccess?.()
    //   }
    // },
  }
})
